import React from 'react';
import { WifiOff, Wifi, Loader2 } from 'lucide-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { Badge } from '@/components/ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * OfflineStatusBadge - A minimal, elegant indicator for the header
 *
 * Shows:
 * - Nothing when online with no pending operations
 * - Subtle badge when offline or with pending operations
 * - Click to trigger manual sync when needed
 */
const OfflineStatusBadge: React.FC = () => {
    const isOnline = useOnlineStatus();
    const { queuedOperations, isSyncing, startSync } = useOfflineSync();

    const isOffline = !isOnline;
    const pendingCount = queuedOperations.length;
    const hasPendingOperations = pendingCount > 0;

    // Hide when online with no pending operations
    if (isOnline && !hasPendingOperations) {
        return null;
    }

    const handleClick = () => {
        if (hasPendingOperations && !isSyncing) {
            startSync();
        }
    };

    // Determine badge appearance
    const getBadgeProps = () => {
        if (isOffline) {
            return {
                variant: 'secondary' as const,
                className: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200',
                icon: <WifiOff className="h-3 w-3" />,
                text: hasPendingOperations
                    ? `Offline • ${pendingCount}`
                    : 'Offline'
            };
        } else if (hasPendingOperations) {
            return {
                variant: 'secondary' as const,
                className: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200',
                icon: isSyncing ?
                    <Loader2 className="h-3 w-3 animate-spin" /> :
                    <Wifi className="h-3 w-3" />,
                text: isSyncing
                    ? 'Syncing...'
                    : `${pendingCount} pending`
            };
        }
        return null;
    };

    const badgeProps = getBadgeProps();
    if (!badgeProps) return null;

    const badge = (
        <Badge
            variant={badgeProps.variant}
            className={`${badgeProps.className} cursor-pointer transition-all duration-200 px-2 py-1 gap-1.5 text-xs font-medium`}
            onClick={handleClick}
        >
            {badgeProps.icon}
            <span>{badgeProps.text}</span>
        </Badge>
    );

    // Tooltip content based on state
    const getTooltipContent = () => {
        if (isOffline && hasPendingOperations) {
            return `You're offline with ${pendingCount} pending changes. They'll sync when you're back online.`;
        } else if (isOffline) {
            return "You're offline. Changes will sync when connection is restored.";
        } else if (hasPendingOperations && isSyncing) {
            return "Syncing your changes to the cloud...";
        } else if (hasPendingOperations) {
            return `${pendingCount} changes waiting to sync. Click to retry now.`;
        }
        return '';
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    {badge}
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">{getTooltipContent()}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default OfflineStatusBadge;
